import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  Loader2,
  AlertCircle,
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  ArrowRight,
  Briefcase,
  Trash2,
  Navigation,
} from 'lucide-react';
import { useState } from 'react';

import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { useUserTripHistory } from '@/hooks/useUserProfile';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { UserTripHistory } from '../../shared/types/user';
import { format } from 'date-fns';

export const Route = createFileRoute('/trips')({
  component: TripsComponent,
});

function TripsComponent() {
  return (
    <ProtectedRoute>
      <TripsContent />
    </ProtectedRoute>
  );
}

function TripsContent() {
  const navigate = useNavigate();
  const {
    tripHistory,
    isLoading: tripsLoading,
    isError: tripsError,
    refetch: refetchTrips,
    deleteTrip,
    isDeleting: isDeletingTrip,
  } = useUserTripHistory();

  // Delete trip confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState<UserTripHistory | null>(
    null
  );

  const handleViewTrip = (tripId: string) => {
    navigate({ to: '/trip/$tripId/edit', params: { tripId } });
  };

  const handleDeleteTrip = (trip: UserTripHistory) => {
    setTripToDelete(trip);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTrip = async () => {
    if (tripToDelete && deleteTrip) {
      try {
        await deleteTrip(tripToDelete.tripId);
        setDeleteConfirmOpen(false);
        setTripToDelete(null);
        // Refetch trips to update the list
        refetchTrips();
      } catch (error) {
        console.error('Failed to delete trip:', error);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">My Trips</h1>
          <p className="text-muted-foreground">
            View and manage all your planned trips. Click on any trip to edit or
            view details.
          </p>
        </div>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle>All Trips</CardTitle>
            <CardDescription>
              Your complete travel history and upcoming adventures
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tripsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-muted-foreground">Loading your trips...</p>
                </div>
              </div>
            ) : tripsError ? (
              <div className="text-center py-12">
                <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Error Loading Trips
                </h3>
                <p className="text-muted-foreground mb-4">
                  We couldn't load your trips. Please try again.
                </p>
                <Button onClick={() => refetchTrips()} variant="outline">
                  <Loader2 className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : tripHistory.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No Trips Yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  You haven't planned any trips yet. Start planning your next
                  adventure!
                </p>
                <Button onClick={() => navigate({ to: '/' })}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Plan Your First Trip
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tripHistory.map((trip: UserTripHistory) => (
                  <motion.div
                    key={trip.tripId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="relative overflow-hidden hover:shadow-warm-lg transition-all duration-300 group border-sand bg-warm-white hover:border-terra/30">
                      {/* Background Gradient Accent */}
                      <div className="absolute inset-0 bg-gradient-to-br from-terra/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Delete Button */}
                      <div className="absolute top-3 right-3 z-10">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-destructive/10 hover:text-destructive text-warm-mid"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteTrip(trip);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <CardContent className="p-6 relative">
                        <div
                          onClick={() => handleViewTrip(trip.tripId)}
                          className="space-y-4 cursor-pointer"
                        >
                          {/* Trip Header */}
                          <div className="space-y-3">
                            <div className="flex items-start justify-between pr-8">
                              <div className="flex-1 space-y-2">
                                <h3 className="text-xl font-bold text-dusk group-hover:text-dusk transition-colors line-clamp-2 font-display">
                                  {trip.tripTitle}
                                </h3>

                                {/* Consolidated destination, waypoint, and status line */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center text-base text-warm-mid group-hover:text-dusk transition-colors">
                                    <MapPin className="h-4 w-4 mr-2 text-terra flex-shrink-0" />
                                    <span className="truncate font-medium">
                                      {trip.destination}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 ml-3">
                                    {/* Waypoint icon */}
                                    <button
                                      className="p-1 rounded-full bg-sage hover:bg-sage-dark border border-sage/60 hover:border-sage-dark backdrop-blur-md transition-all duration-300 hover:scale-110"
                                      title="View on Map"
                                      onClick={e => {
                                        e.stopPropagation();
                                        // TODO: Add map functionality
                                      }}
                                    >
                                      <Navigation className="w-3 h-3 text-white" />
                                    </button>

                                    {/* Status badge */}
                                    <Badge
                                      variant={
                                        trip.status === 'completed'
                                          ? 'default'
                                          : trip.status === 'booked'
                                            ? 'secondary'
                                            : trip.status === 'cancelled'
                                              ? 'destructive'
                                              : 'outline'
                                      }
                                      className={`text-xs ${
                                        trip.status === 'completed'
                                          ? 'bg-sage/15 text-sage-dark border-sage/40'
                                          : trip.status === 'booked'
                                            ? 'bg-terra/15 text-terra border-terra/40'
                                            : trip.status === 'cancelled'
                                              ? 'bg-rust/15 text-rust border-rust/40'
                                              : 'bg-sand text-warm-mid border-sand'
                                      }`}
                                    >
                                      {trip.status.charAt(0).toUpperCase() +
                                        trip.status.slice(1)}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Trip Details */}
                          <div className="space-y-3 py-2">
                            {/* Trip Dates */}
                            <div className="flex items-center text-sm text-warm-mid">
                              <Calendar className="h-4 w-4 mr-2 text-terra" />
                              <span>
                                {(() => {
                                  try {
                                    const startDate = new Date(trip.startDate);
                                    const endDate = new Date(trip.endDate);

                                    // Check if dates are valid
                                    if (
                                      isNaN(startDate.getTime()) ||
                                      isNaN(endDate.getTime())
                                    ) {
                                      return 'Dates to be determined';
                                    }

                                    // Check if dates are just placeholder "now" dates (same date)
                                    if (
                                      startDate.getTime() === endDate.getTime()
                                    ) {
                                      return 'Dates to be determined';
                                    }

                                    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
                                  } catch {
                                    return 'Dates to be determined';
                                  }
                                })()}
                              </span>
                            </div>

                            {/* Trip Duration */}
                            <div className="flex items-center text-sm text-warm-mid">
                              <Clock className="h-4 w-4 mr-2 text-terra" />
                              <span>
                                {(() => {
                                  try {
                                    const startDate = new Date(trip.startDate);
                                    const endDate = new Date(trip.endDate);

                                    // Check if dates are valid
                                    if (
                                      isNaN(startDate.getTime()) ||
                                      isNaN(endDate.getTime())
                                    ) {
                                      return 'Duration TBD';
                                    }

                                    // Check if dates are just placeholder "now" dates (same date)
                                    if (
                                      startDate.getTime() === endDate.getTime()
                                    ) {
                                      return 'Duration TBD';
                                    }

                                    const days = Math.ceil(
                                      (endDate.getTime() -
                                        startDate.getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    );
                                    return `${Math.max(days, 1)} days`;
                                  } catch {
                                    return 'Duration TBD';
                                  }
                                })()}
                              </span>
                            </div>

                            {/* Trip Cost */}
                            {trip.totalCost && (
                              <div className="flex items-center text-sm text-warm-mid">
                                <DollarSign className="h-4 w-4 mr-2 text-terra" />
                                <span>
                                  {trip.totalCost.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: trip.currency || 'USD',
                                  })}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Action Footer */}
                          <div className="flex items-center justify-between pt-3 border-t border-sand">
                            <span className="text-xs text-warm-mid">
                              Created{' '}
                              {format(new Date(trip.createdAt), 'MMM d, yyyy')}
                            </span>
                            <div className="flex items-center text-sm text-terra group-hover:text-rust transition-colors">
                              <span className="mr-1 font-medium">
                                Manage Trip
                              </span>
                              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="bg-warm-white border-sand">
            <DialogHeader>
              <DialogTitle className="text-dusk">Delete Trip</DialogTitle>
              <DialogDescription className="text-warm-mid">
                Are you sure you want to delete "{tripToDelete?.tripTitle}"?
                This action cannot be undone and will permanently remove all
                trip data.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                className="border-sand text-warm-mid hover:bg-sand/50"
                disabled={isDeletingTrip}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteTrip}
                disabled={isDeletingTrip}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeletingTrip && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Delete Trip
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
